import { PrinterOutlined } from '@ant-design/icons';
import { Alert, Spin, message } from 'antd';
import dayjs from 'dayjs';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import orderApi from '../../apis/order';
import logo from '../../assets/image/logo_1000px.png';
import { ButtonType } from '../../constants/style';
import { OrderInvoice } from '../../interfaces/Invoice';
import { PageContainer } from '../../styles/Global';
import { $border, $lightgrey, $primary } from '../../styles/Variables';
import Button from '../Button/Button';
import PageHeader from '../PageHeader/PageHeader';

const formatMoney = (value: number) =>
  `$${(value ?? 0).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const display = (value?: string | null) => value?.trim() || 'N/A';

const InvoiceShell = styled.div`
  background: #fff;
  border: 1px solid ${$border};
  border-radius: 12px;
  padding: 28px;
  max-width: 1100px;
`;

const InvoiceHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 28px;
  gap: 16px;
`;

const InvoiceTitle = styled.h1`
  font-size: 22px;
  font-weight: 700;
  color: #101828;
  margin: 0;
  word-break: break-all;
`;

const InvoiceDate = styled.p`
  margin-top: 6px;
  color: ${$lightgrey};
  font-size: 13px;
`;

const Brand = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
`;

const BrandLogo = styled.img`
  height: 48px;
  width: auto;
  object-fit: contain;
  display: block;
`;

const BrandName = styled.div`
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.04em;
  color: ${$lightgrey};
  text-align: right;
`;

const TopGrid = styled.div`
  display: grid;
  grid-template-columns: 1.2fr 1fr 0.9fr;
  gap: 20px;
  margin-bottom: 28px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const InfoCard = styled.div`
  border: 1px solid ${$border};
  border-radius: 10px;
  padding: 20px 22px;
  background: #fff;
`;

const CompanyName = styled.div`
  font-weight: 700;
  font-size: 15px;
  margin-bottom: 16px;
  color: #101828;
`;

const CompanyLine = styled.div`
  color: #344054;
  font-size: 13px;
  line-height: 1.6;
  padding: 12px 0;
  border-bottom: 1px solid ${$border};

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }

  &:first-of-type {
    padding-top: 0;
  }
`;

const SectionTitle = styled.div`
  font-weight: 700;
  font-size: 14px;
  margin-bottom: 16px;
  color: #101828;
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 16px;
  padding: 12px 0;
  border-bottom: 1px solid ${$border};
  font-size: 13px;
  line-height: 1.5;

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }

  span:first-child {
    color: ${$lightgrey};
    flex-shrink: 0;
  }

  span:last-child {
    color: #101828;
    text-align: right;
    word-break: break-word;
  }
`;

const PaymentTotal = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid ${$border};
  font-weight: 700;
  font-size: 15px;
  color: #101828;
`;

const TableWrap = styled.div`
  border: 1px solid ${$border};
  border-radius: 10px;
  overflow: hidden;
`;

const TableHead = styled.div`
  display: grid;
  grid-template-columns: 2.4fr 0.6fr 1.6fr 0.8fr;
  gap: 12px;
  padding: 12px 16px;
  background: #f9fafb;
  color: ${$lightgrey};
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
`;

const TableRow = styled.div`
  display: grid;
  grid-template-columns: 2.4fr 0.6fr 1.6fr 0.8fr;
  gap: 12px;
  padding: 14px 16px;
  border-top: 1px solid ${$border};
  align-items: center;
`;

const ProductCell = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  min-width: 0;
`;

const Thumb = styled.img`
  width: 48px;
  height: 48px;
  object-fit: cover;
  border-radius: 6px;
  border: 1px solid ${$border};
  flex-shrink: 0;
  background: #f2f4f7;
`;

const ThumbPlaceholder = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 6px;
  border: 1px solid ${$border};
  background: #f2f4f7;
  flex-shrink: 0;
`;

const Sku = styled.div`
  color: ${$primary};
  font-size: 12px;
  font-weight: 600;
  word-break: break-all;
`;

const ProductTypeLabel = styled.div`
  color: ${$lightgrey};
  font-size: 12px;
  font-weight: 500;
  margin-top: 2px;
`;

const ProductName = styled.div`
  color: #101828;
  font-size: 13px;
  margin-top: 4px;
`;

const Qty = styled.div`
  font-weight: 600;
  color: #101828;
`;

const Tracking = styled.div`
  font-size: 12px;
  color: #344054;
  word-break: break-all;

  .service {
    color: ${$lightgrey};
    margin-bottom: 2px;
  }

  a {
    color: ${$primary};
  }
`;

const LineTotal = styled.div`
  font-weight: 700;
  text-align: right;
  color: #101828;
`;

const FooterActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
`;

const InvoiceView = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const invoiceRef = useRef<HTMLDivElement>(null);
  const [invoice, setInvoice] = useState<OrderInvoice | null>(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const loadInvoice = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const data = await orderApi.getOrderInvoice(id);
      setInvoice(data);
    } catch (e: any) {
      message.error(e?.message || 'Failed to load invoice');
      navigate(-1);
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    loadInvoice();
  }, [loadInvoice]);

  const handlePrintPdf = async () => {
    if (!invoiceRef.current || !invoice) return;
    setExporting(true);
    try {
      const canvas = await html2canvas(invoiceRef.current, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        logging: false,
      });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'pt',
        format: 'a4',
      });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 24;
      const usableWidth = pageWidth - margin * 2;
      const imgHeight = (canvas.height * usableWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = margin;

      pdf.addImage(imgData, 'PNG', margin, position, usableWidth, imgHeight);
      heightLeft -= pageHeight - margin * 2;

      while (heightLeft > 0) {
        position = margin - (imgHeight - heightLeft);
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', margin, position, usableWidth, imgHeight);
        heightLeft -= pageHeight - margin * 2;
      }

      const filename = `invoice-${invoice.invoiceNumber}.pdf`;
      pdf.save(filename);
      message.success('PDF downloaded');
    } catch (e) {
      console.error(e);
      message.error('Failed to generate PDF');
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <Spin />
      </PageContainer>
    );
  }

  if (!invoice) {
    return null;
  }

  return (
    <PageContainer>
      <PageHeader title="Invoice" showBackButton />

      {!invoice.profileComplete && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16, maxWidth: 1100 }}
          message="Invoice information incomplete"
          description={
            <span>
              Fill in your billing details so invoices show the correct
              &quot;Invoice to&quot; section.{' '}
              <Link to="/invoice-info">Update invoice information</Link>
            </span>
          }
        />
      )}

      <InvoiceShell ref={invoiceRef}>
        <InvoiceHeader>
          <div>
            <InvoiceTitle>Invoice #{invoice.invoiceNumber}</InvoiceTitle>
            <InvoiceDate>
              {dayjs(invoice.createdAt).format('MMM D, YYYY h:mm A')}
            </InvoiceDate>
          </div>
          <Brand>
            <BrandLogo src={logo} alt="HD Sticker" />
            <BrandName>{invoice.company.name}</BrandName>
          </Brand>
        </InvoiceHeader>

        <TopGrid>
          <InfoCard>
            <CompanyName>{invoice.company.name}</CompanyName>
            <CompanyLine>{invoice.company.address}</CompanyLine>
            <CompanyLine>{invoice.company.phone}</CompanyLine>
            <CompanyLine>{invoice.company.email}</CompanyLine>
            {invoice.company.website ? (
              <CompanyLine>{invoice.company.website}</CompanyLine>
            ) : null}
          </InfoCard>

          <InfoCard>
            <SectionTitle>Invoice to</SectionTitle>
            <DetailRow>
              <span>Business name</span>
              <span>{display(invoice.billTo.businessName)}</span>
            </DetailRow>
            <DetailRow>
              <span>Full name</span>
              <span>{display(invoice.billTo.fullName)}</span>
            </DetailRow>
            <DetailRow>
              <span>Address</span>
              <span>{display(invoice.billTo.address)}</span>
            </DetailRow>
            <DetailRow>
              <span>Email</span>
              <span>{display(invoice.billTo.email)}</span>
            </DetailRow>
            <DetailRow>
              <span>Phone</span>
              <span>{display(invoice.billTo.phone)}</span>
            </DetailRow>
          </InfoCard>

          <InfoCard>
            <SectionTitle>Payment details</SectionTitle>
            <DetailRow>
              <span>Transaction code</span>
              <span>{invoice.invoiceNumber}</span>
            </DetailRow>
            <DetailRow>
              <span>Subtotal</span>
              <span>{formatMoney(invoice.payment.subtotal)}</span>
            </DetailRow>
            <DetailRow>
              <span>Shipping fee</span>
              <span>{formatMoney(invoice.payment.shippingFee)}</span>
            </DetailRow>
            <PaymentTotal>
              <span>Total due</span>
              <span>{formatMoney(invoice.payment.total)}</span>
            </PaymentTotal>
          </InfoCard>
        </TopGrid>

        <TableWrap>
          <TableHead>
            <div>Details</div>
            <div>Quantity</div>
            <div>Tracking number</div>
            <div style={{ textAlign: 'right' }}>Line total</div>
          </TableHead>
          {invoice.lines.map((line, index) => (
            <TableRow key={`${line.sku || line.productName}-${index}`}>
              <ProductCell>
                {line.imageUrl ? (
                  <Thumb src={line.imageUrl} alt={line.productName} />
                ) : (
                  <ThumbPlaceholder />
                )}
                <div>
                  <Sku>SKU: {display(line.sku)}</Sku>
                  {line.productType ? (
                    <ProductTypeLabel>{line.productType}</ProductTypeLabel>
                  ) : null}
                  <ProductName>{line.productName}</ProductName>
                </div>
              </ProductCell>
              <Qty>{line.quantity}</Qty>
              <Tracking>
                {line.trackingNumber ? (
                  <>
                    {line.shippingService ? (
                      <div className="service">{line.shippingService}</div>
                    ) : null}
                    <div>{line.trackingNumber}</div>
                  </>
                ) : (
                  <div>—</div>
                )}
              </Tracking>
              <LineTotal>{formatMoney(line.lineTotal)}</LineTotal>
            </TableRow>
          ))}
          {invoice.lines.length === 0 && (
            <TableRow>
              <div style={{ gridColumn: '1 / -1', color: $lightgrey }}>
                No line items
              </div>
            </TableRow>
          )}
        </TableWrap>
      </InvoiceShell>

      <FooterActions>
        <Button type={ButtonType.OUTLINE} onClick={() => navigate(-1)}>
          Cancel
        </Button>
        <Button
          type={ButtonType.PRIMARY}
          loading={exporting}
          onClick={handlePrintPdf}
        >
          <PrinterOutlined />
          <span className="font-size--14">Print to PDF</span>
        </Button>
      </FooterActions>
    </PageContainer>
  );
};

export default InvoiceView;
